import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Checkbox,
  FormControlLabel,
  Button,
  Alert,
  Snackbar,
  CircularProgress,
  Chip
} from '@mui/material';
import { API_BASE_URL } from '../config/api';

interface DeviceItem {
  id: string;
  name?: string;
  status?: string;
  ip?: string;
}

interface DevicePoolResponse {
  devices: DeviceItem[];
  selectedDevices: string[];
  maxDevices: number;
}

export default function CashOnlyDevicePool() {
  const [loading, setLoading] = useState(true);
  const [devices, setDevices] = useState<DeviceItem[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    open: false,
    message: '',
    severity: 'info'
  });

  const fetchJson = async (url: string, options?: RequestInit) => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 10000);
    try {
      const response = await fetch(url, {
        ...options,
        credentials: 'include',
        signal: controller.signal
      });
      if (!response.ok) {
        throw new Error(`Request failed (${response.status})`);
      }
      return await response.json();
    } finally {
      clearTimeout(timeout);
    }
  };

  const fetchPool = async () => {
    try {
      const data = await fetchJson(`${API_BASE_URL}/api/queue/cash-loop/device-pool`);
      if (data.success) {
        const payload: DevicePoolResponse = data.data;
        setDevices(payload.devices || []);
        setSelected(payload.selectedDevices || []);
      } else {
        setSnackbar({ open: true, message: data.error || 'Failed to load device pool', severity: 'error' });
      }
    } catch (error: any) {
      setSnackbar({ open: true, message: error.message || 'Failed to load device pool', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPool();
  }, []);

  const selectionHint = useMemo(() => {
    const minRequired = 1;
    if (selected.length < minRequired) return 'Select at least 1 device';
    if (selected.length > 3) return 'Select no more than 3 devices';
    return 'Selection OK';
  }, [selected.length, devices.length]);

  const toggleDevice = (id: string) => {
    setSelected(prev => {
      if (prev.includes(id)) return prev.filter(d => d !== id);
      return [...prev, id];
    });
  };

  const handleSave = async () => {
    const minRequired = 1;
    if (selected.length < minRequired || selected.length > 3) {
      setSnackbar({ open: true, message: 'Please select 1–3 devices', severity: 'error' });
      return;
    }
    try {
      const data = await fetchJson(`${API_BASE_URL}/api/queue/cash-loop/device-pool`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ devices: selected, maxDevices: selected.length })
      });
      if (data.success) {
        setSnackbar({ open: true, message: 'Cash-only device pool updated', severity: 'success' });
        fetchPool();
      } else {
        setSnackbar({ open: true, message: data.error || 'Save failed', severity: 'error' });
      }
    } catch (error: any) {
      setSnackbar({ open: true, message: error.message || 'Save failed', severity: 'error' });
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h4" gutterBottom>
        Cash-Only Device Pool
      </Typography>

      <Alert severity="info" sx={{ mb: 2 }}>
        All devices are shown. Recommended size: 2–3 devices.
      </Alert>

      <Card>
        <CardContent>
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body1">Selected: {selected.length}</Typography>
            <Chip
              label={selectionHint}
              color={selected.length >= 2 && selected.length <= 3 ? 'success' : 'warning'}
              size="small"
            />
          </Box>

          <Grid container spacing={2}>
            {devices.map(device => (
              <Grid item xs={12} md={6} key={device.id}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selected.includes(device.id)}
                      onChange={() => toggleDevice(device.id)}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1">{device.name || device.id}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {device.status || 'unknown'} {device.ip ? `| ${device.ip}` : ''}
                      </Typography>
                    </Box>
                  }
                />
              </Grid>
            ))}
          </Grid>

          <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
            <Button variant="contained" onClick={handleSave}>
              Save Device Pool
            </Button>
            <Button variant="outlined" onClick={fetchPool}>
              Refresh
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
